import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { api, errMsg } from "../lib/api";
import { inr, num, todayIST } from "../lib/format";
import { Field, PageHead, StickyBar } from "../components/FormBits";

export default function PurchaseReturn() {
  const { grnId } = useParams();
  const navigate = useNavigate();
  const [grn, setGrn] = useState(null);
  const [f, setF] = useState({ date: todayIST(), debit_note_no: "", reason: "" });
  const [qty, setQty] = useState({});
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    api.get(`/grns/${grnId}`).then((r) => setGrn(r.data)).catch((e) => toast.error(errMsg(e)));
  }, [grnId]);
  if (!grn) return <p className="text-muted-foreground">Loading…</p>;
  const lines = grn.items.filter((i) => i.qty_received > 0);
  const total = lines.reduce((a, i) => a + (Number(qty[i.po_item_id]) || 0) * i.rate * (1 + i.gst / 100), 0);
  const save = async () => {
    const items = lines.map((i) => ({ po_item_id: i.po_item_id, qty: Number(qty[i.po_item_id]) || 0 })).filter((i) => i.qty > 0);
    if (!items.length) return toast.error("Enter a return quantity for at least one item");
    setSaving(true);
    try {
      const { data } = await api.post(`/grns/${grnId}/returns`, { ...f, items });
      toast.success(`${data.pr_no} saved`);
      navigate("/grns");
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setSaving(false);
    }
  };
  return (
    <div className="space-y-4" data-testid="purchase-return-form">
      <PageHead kicker={`${grn.grn_no} · ${grn.po_no}`} title="Purchase return" sub={`Goods sent back to ${grn.vendor_name}. Stock is reduced.`} />
      <section className="panel grid grid-cols-1 gap-4 p-4 min-[400px]:grid-cols-2">
        <Field label="Return date"><input type="date" className="field" value={f.date} onChange={(e) => setF({ ...f, date: e.target.value })} data-testid="return-date-input" /></Field>
        <Field label="Debit note no"><input className="field" value={f.debit_note_no} onChange={(e) => setF({ ...f, debit_note_no: e.target.value })} data-testid="return-dn-input" /></Field>
        <div className="min-[400px]:col-span-2"><Field label="Reason"><input className="field" value={f.reason} onChange={(e) => setF({ ...f, reason: e.target.value })} placeholder="Damaged, wrong shade…" data-testid="return-reason-input" /></Field></div>
      </section>
      {lines.map((i, n) => (
        <div key={i.po_item_id} className="panel space-y-2 p-4" data-testid={`return-line-${n}`}>
          <p className="font-semibold">{i.description}{i.colour ? ` (${i.colour})` : ""} {i.code && <span className="mono text-xs text-muted-foreground">[{i.code}]</span>}</p>
          <p className="mono text-xs text-muted-foreground">Received {num(i.qty_received)} {i.uom} · {inr(i.rate)} / {i.uom} · {i.gst}% GST</p>
          <Field label={`Qty to return (${i.uom})`}>
            <input className="field mono" type="number" inputMode="decimal" min="0" step="any" value={qty[i.po_item_id] || ""}
              onChange={(e) => setQty({ ...qty, [i.po_item_id]: e.target.value })} data-testid={`return-line-${n}-qty-input`} />
          </Field>
        </div>
      ))}
      <StickyBar onSave={save} saving={saving} label="Save return" testid="return">
        <p className="mono-label text-muted-foreground">Return value incl. GST</p>
        <p className="mono text-xl font-bold" data-testid="return-total">{inr(total)}</p>
      </StickyBar>
    </div>
  );
}
